import React from 'react';
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as MenuItemIco} from "../../../../../../../../images/icons/menus/menu-item.svg";
import {ReactComponent as MenuItemDarkIco} from "../../../../../../../../images/icons/menus/menu-item-dark.svg";
import {ReactComponent as CheckCircleIco} from "../../../../../../../../images/icons/check-circle.svg";
import {ReactComponent as PlusIco} from "../../../../../../../../images/icons/plus.svg";
import {ReactComponent as MinusIco} from "../../../../../../../../images/icons/minus.svg";
import Money from "../../../../../../../components/Common/Money";
import AppConfig from "../../../../../../../services/AppConfig";
import {createKeyDownHandler} from "../../../../../../../utils/keyDownHanlder";
import {connect} from "react-redux";
import {RootState} from "../../../../../../../../reducers";
import {getStoreConfig} from "../../../../../../../services/Store/reducer";
import StoreUtils from "../../../../../../../services/Store/StoreUtils";

interface OwnProps {
    modifierGroupOption: Record<string, any>;
    modifierGroup: Record<string, any>;
    changeQuantity: (quantity: number, option: Record<string, any>) => void;
    optionState: Record<string, any> | undefined;
    isProductAvailable: boolean;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductModifierGroupSimpleOption = ({
                                              modifierGroupOption,
                                              modifierGroup,
                                              changeQuantity,
                                              optionState,
                                              isProductAvailable,
                                              config
                                          }: Props) => {
    const quantity = optionState?.quantity ?? 0;
    const isReadonly = StoreUtils.isOrderingReadonly(config);
    const displayQuantity = quantity - (modifierGroupOption.min_permitted ?? 0);
    const checked = isReadonly ? false : displayQuantity > 0;
    const {t} = useTranslation();
    const disabled = isReadonly ? false : modifierGroupOption.disabled || !isProductAvailable;

    const isIncreaseQuantityDisabled = () => {
        if (modifierGroup.max_permitted && modifierGroup.max_permitted <= modifierGroup.quantity) return true;
        if (modifierGroupOption.max_permitted && modifierGroupOption.max_permitted <= quantity) return true;
    }

    const handleChangeQuantity = (type: "increase" | "decrease", event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event?.preventDefault();
        event?.stopPropagation();

        const newQuantity = type === "increase" ? quantity + 1 : quantity - 1;
        changeQuantity(newQuantity, modifierGroupOption);
    }

    const getPrice = () => {
        if (modifierGroup.adapted_charge_above) {
            if (modifierGroup.adapted_quantity >= modifierGroup.adapted_charge_above) {
                if (!optionState) return modifierGroupOption.price;
                return optionState.total_price || optionState.unit_total_price;
            } else {
                return 0
            }
        } else {
            if (!optionState) return modifierGroupOption.price;

            return optionState.total_price;
        }
    }

    const handleOnClickOption = () => {
        if (isReadonly) return;

        if (isIncreaseQuantityDisabled() || disabled) return;

        handleChangeQuantity("increase");
    };

    const handleOnOptionKeyDown = createKeyDownHandler<HTMLDivElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        action: () => handleOnClickOption()
    });

    const handleChangeQuantityOnKeyDown = (type: "increase" | "decrease") => createKeyDownHandler<HTMLDivElement>({
        keys: ["Enter", " "],
        preventDefault: true,
        stopPropagation: true,
        action: () => handleChangeQuantity(type)
    });

    const isItemClickable = () => {
        if (isReadonly) return false;

        return !(isIncreaseQuantityDisabled() || !isProductAvailable);
    }

    return (
        <div onClick={handleOnClickOption} onKeyDown={handleOnOptionKeyDown} tabIndex={0}
             className={`product-item-option product-modifier-group-option ${checked ? "active" : ""} ${disabled ? "disabled" : ""} ${isItemClickable() ? "" : "not-clickable"}`}>
            {checked && !modifierGroupOption.disabled && (
                <div className="check-circle">
                    <CheckCircleIco/>
                </div>
            )}
            <div className="product-image">
                {modifierGroupOption.image_small ? (
                    <img alt={t("common.word.product_image")}
                         src={modifierGroupOption.image_small}/>
                ) : (
                    AppConfig.isDarkMode() ? <MenuItemDarkIco/> : <MenuItemIco/>
                )}
            </div>
            <div className="d-flex flex-column w-100">
                {modifierGroupOption.disabled &&
                    <small className="text-warning fw-normal">{t("modules.menu.product_not_available.field")}</small>}
                <h1 className="h6">
                    {modifierGroupOption.name}
                </h1>
                {modifierGroupOption.description && (
                    <p className="fw-normal">{modifierGroupOption.description}</p>
                )}
                <div className="d-flex justify-content-between align-items-center gap-3 w-100">
                    <div className="prices-container d-flex flex-column">
                        <p>
                            <Money
                                value={getPrice()}
                                currency='PLN'/>
                        </p>
                        {
                            quantity > 1 && (
                                <small className="d-inline-block">
                                    (<Money value={optionState?.unit_total_price ?? 0}
                                            currency='PLN'/> / {t("modules.menu.field.piece.title")})
                                </small>
                            )
                        }
                    </div>
                    {!disabled && !StoreUtils.isOrderingReadonly(config) && (
                        <div className="input-group input-group-sm input-group-quantity gap-0">
                            <div className="input-group-prepend" onKeyDown={handleChangeQuantityOnKeyDown("decrease")}>
                                <button type="button" className="btn btn-default btn-icon"
                                        onClick={(event) => handleChangeQuantity("decrease", event)}
                                        disabled={quantity === 0 || quantity <= modifierGroupOption.min_permitted || !isProductAvailable}
                                        aria-label={`${t("common.action.decrease_quantity")} ${modifierGroupOption.name}`}>
                                    <MinusIco aria-hidden="true"/>
                                </button>
                            </div>
                            <span className="input-group-text">{displayQuantity}</span>
                            <div className="input-group-append" onKeyDown={handleChangeQuantityOnKeyDown("increase")}>
                                <button type="button" className="btn btn-default btn-icon"
                                        onClick={(event) => handleChangeQuantity("increase", event)}
                                        disabled={isIncreaseQuantityDisabled() || !isProductAvailable}
                                        aria-label={`${t("common.action.increase_quantity")} ${modifierGroupOption.name}`}>
                                    <PlusIco aria-hidden="true"/>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

const mapStateToProps = (state: RootState) => ({
    config: getStoreConfig(state.config),
});

const connector = connect(mapStateToProps);
export default withTranslation()(connector(ProductModifierGroupSimpleOption));
